import React, { useEffect, useContext, useState } from "react";
import { PropTypes } from "prop-types";
import { withTranslation } from "react-i18next";
import Select from "react-select";
import { mdiPlayCircleOutline, mdiClose } from "@mdi/js";
import { Icon } from "@mdi/react";

import SegmentedControl from "components/utils/SegmentedControl/SegmentedControl";
import Player from "components/player/Player";
import { EXTERNAL_SERVICE_TYPE, EXTERNAL_TYPE } from "components/content/Type";

import "css/admin/utils/form-external.scss";
import MediaDuration from "../contentTabs/media/MediaDuration";
import MediaEPG from "../contentTabs/media/MediaEPG";
import ContentContext from "../../contexts/ContentContext";
import Button from "../button/Button";

const EXTERNAL_SERVICES = [
  { value: EXTERNAL_SERVICE_TYPE.YOUTUBE, label: "Youtube" },
  { value: EXTERNAL_SERVICE_TYPE.DAILYMOTION, label: "DailyMotion" },
  { value: EXTERNAL_SERVICE_TYPE.FACEBOOK, label: "Facebook" },
  { value: EXTERNAL_SERVICE_TYPE.ARTE, label: "Arte" },
  { value: EXTERNAL_SERVICE_TYPE.VIMEO, label: "Vimeo" },
  { value: EXTERNAL_SERVICE_TYPE.FRANCETV, label: "FranceTV" },
];

const getService = (player_code) => {
  const service = player_code ? player_code.split("|")[0] : null;
  return service ? EXTERNAL_SERVICES.find((s) => s.value === service) : null;
};

const getServiceUrl = (player_code) =>
  player_code ? player_code.split("|")[1] : "";

function ContentAdminExternalForm({ disabled, onChange, t, item }) {
  const { onItemUpdate } = useContext(ContentContext);

  const [inputType, setInputType] = useState(
    item.external_stream?.type === EXTERNAL_TYPE.EMBEDDED ? 1 : 0,
  );
  const [url, setUrl] = useState(
    item.external_stream?.type === "stream" ? item.external_stream.url : "",
  );
  const [playerCode, setPlayerCode] = useState(
    item.external_stream?.type === EXTERNAL_TYPE.EMBEDDED
      ? item.external_stream.player_code
      : "",
  );
  const [switchDisabled, setSwitchDisabled] = useState(
    item.external_stream?.uuid !== undefined,
  );
  const [previewOpened, setPreviewOpened] = useState(false);
  const [service, setService] = useState(
    item.external_stream?.type === EXTERNAL_TYPE.EMBEDDED
      ? getService(item.external_stream.player_code)
      : null,
  );
  const [serviceUrlID, setServiceUrlID] = useState(
    item.external_stream?.type === EXTERNAL_TYPE.EMBEDDED
      ? getServiceUrl(item.external_stream.player_code)
      : "",
  );

  useEffect(() => {
    setInputType(item.external_stream?.type === EXTERNAL_TYPE.EMBEDDED ? 1 : 0);
    setUrl(
      item.external_stream?.type === "stream" ? item.external_stream.url : "",
    );
    setPlayerCode(
      item.external_stream?.type === EXTERNAL_TYPE.EMBEDDED
        ? item.external_stream.player_code
        : "",
    );
    setSwitchDisabled(item.external_stream?.uuid !== undefined);
    setService(
      item.external_stream?.type === EXTERNAL_TYPE.EMBEDDED
        ? getService(item.external_stream.player_code)
        : null,
    );
    setServiceUrlID(
      item.external_stream?.type === EXTERNAL_TYPE.EMBEDDED
        ? getServiceUrl(item.external_stream.player_code)
        : "",
    );
    setPreviewOpened(false);
  }, [item.external_stream]);

  const getUrlType = (url) => {
    const ext = url.split(/#|\?/g)[0].split(".").pop().trim(); // TODO RegExp: '/^(?:.[^?]*)\/(?:(?:.[^/?]*)\.(?<extension>.[^/?]*))(?:\?.*)?$/g'

    if (ext === "m3u" || ext === "m3u8") return "m3u8";
    return "mp4";
  };

  const onChangeCDNUrl = (e) => {
    const new_external_stream = {
      ...item.external_stream,
      type: "stream",
      url: e.target.value,
      url_type: getUrlType(e.target.value),
      player_code: null,
    };

    onChange(new_external_stream, "external_stream");
  };

  const onChangeServiceUrlID = (e) => {
    const { value } = e.target;

    // Split player code into tokens
    const playerCodeTokens = playerCode ? playerCode.split("|") : [null, null];

    // Overwrite second entry: url or id, by our new value, ie 'https://www.youtube.com/embed/BYE8h8BqGGg' or 'BYE8h8BqGGg'
    playerCodeTokens[1] = value;
    const new_external_stream = {
      ...item.external_stream,
      type: EXTERNAL_TYPE.EMBEDDED,
      player_code: playerCodeTokens.join("|"),
      url: null,
      url_type: null,
    };

    onChange(new_external_stream, "external_stream");
  };

  const onService = (data) => {
    const { value } = data;

    // Split player code into tokens
    const playerCodeTokens = playerCode ? playerCode.split("|") : [null, null];
    // Overwrite first entry: service, by our new value, ie: 'YOUTUBE'
    playerCodeTokens[0] = value;
    const new_external_stream = {
      ...item.external_stream,
      type: EXTERNAL_TYPE.EMBEDDED,
      player_code: playerCodeTokens.join("|"),
      url: null,
      url_type: null,
    };

    setService(data);

    onChange(new_external_stream, "external_stream");
  };

  const onChangeType = (type) => {
    // Reset all data when changing type
    const new_external_stream = {
      ...item.external_stream,
      type,
      url_type: null,
      url: null,
      player_code: null,
    };

    onChange(new_external_stream, "external_stream");
  };

  const onInputTypeChange = (value) => {
    if (value !== inputType) {
      // setInputType(value)
      onChangeType(value === 0 ? "stream" : EXTERNAL_TYPE.EMBEDDED);
    }
  };

  const onTogglePreview = () => {
    setPreviewOpened(!previewOpened);
  };

  // TODO better link between tabs definition and BE available entries (enum or something else)
  const tabs = [
    { label: t("stream"), value: 0 },
    { label: t("external"), value: 1 },
  ];

  return (
    <>
      <div className="editor-form-external-container editor-form-section">
        <div className="editor-form-section">
          <div className="editor-form-section-title">
            <span className="external-type">{t("source")}</span>
            <SegmentedControl
              disabled={switchDisabled || disabled}
              tabs={tabs}
              onChange={onInputTypeChange}
              selected={inputType}
            />
          </div>
        </div>
        {inputType === 0 && (
          <div className="editor-form-section !mb-0 border-b pb-[0.78125rem]">
            <div className="editor-form-external-url">
              <div className="editor-form-section-title">{t("source_url")}</div>
              <div className="editor-form-external-box">
                <div className="editor-form-input-box-h">
                  <input
                    type="text"
                    value={url}
                    disabled={disabled}
                    onChange={onChangeCDNUrl}
                  />
                </div>
                <Button
                  isDisabled={
                    !item.external_stream?.uuid || item.status === "archived"
                  }
                  className="ml-0!"
                  onClick={onTogglePreview}
                  size="xs"
                  type="button"
                >
                  {previewOpened ? (
                    <Icon path={mdiClose} size={0.7} />
                  ) : (
                    <Icon path={mdiPlayCircleOutline} size={0.7} />
                  )}
                  {t(previewOpened ? "hideVideo" : "showVideo")}
                </Button>
              </div>
            </div>
            <MediaEPG item={item} onItemUpdate={onItemUpdate} />
          </div>
        )}
        {inputType === 1 && (
          <div className="editor-form-external-url border-b pb-[0.9375rem]">
            <div className="editor-form-section">
              <div className="editor-form-section-title">
                {t("external_service")}
              </div>
              <div className="editor-form-input" style={{ maxWidth: "150px" }}>
                <Select
                  name="form-field-name"
                  closeMenuOnSelect
                  value={service}
                  onChange={onService}
                  options={EXTERNAL_SERVICES}
                  placeholder={t("select")}
                  className="editor-select"
                  classNamePrefix="slct"
                />
              </div>
            </div>
            <div className="editor-form-section">
              <div className="editor-form-section-title">
                {t("external_url_id")}
              </div>
              <div className="editor-form-external-box">
                <div className="editor-form-input-box-h">
                  <input
                    type="text"
                    value={serviceUrlID}
                    disabled={disabled}
                    onChange={onChangeServiceUrlID}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {previewOpened && (
          <div className="mt-5 mb-2 aspect-video w-full">
            <Player item={item} isFullSize />
          </div>
        )}
      </div>
      <MediaDuration item={item} onItemUpdate={onItemUpdate} />
    </>
  );
}

ContentAdminExternalForm.propTypes = {
  disabled: PropTypes.bool,
  item: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default withTranslation(
  "editor-content",
  "editor-errors",
  "popup",
  "common",
)(ContentAdminExternalForm);
